<template>
  <div class="verify-container">
    <div class="verify-header">
      <h1>Identity Verification</h1>
      <p class="subtitle">
        Complete identity verification to unlock payments and withdrawals.
      </p>
    </div>

    <div v-if="loading" class="status-panel">
      <div class="spinner"></div>
      <p>Loading verification…</p>
    </div>

    <div v-else-if="terminalStatus === 'success'" class="status-panel success">
      <h2>Verification Complete</h2>
      <p>Your identity has been verified. Redirecting…</p>
    </div>

    <div v-else-if="terminalStatus === 'review'" class="status-panel review">
      <h2>Awaiting Review</h2>
      <p>
        Your documents have been submitted and are now under manual review by
        our team. You can continue to use the app while we complete the review —
        we'll notify you once it's done.
      </p>
      <router-link to="/games" class="return-button"
        >Continue to games</router-link
      >
    </div>

    <div v-else-if="terminalStatus === 'failed'" class="status-panel error">
      <h2>Verification Failed</h2>
      <p>
        {{
          errorMessage || "Your identity verification could not be completed."
        }}
      </p>
      <router-link to="/login" class="return-button"
        >Return to login</router-link
      >
    </div>

    <div v-show="showWidget" class="widget-wrap">
      <div id="vv-kyc"></div>
    </div>

    <div class="help-section">
      <h3>Need help?</h3>
      <ul>
        <li>Have a valid government-issued ID ready</li>
        <li>Ensure good lighting for document photos</li>
        <li>Verification typically takes 2–5 minutes</li>
      </ul>
      <p class="note">
        <strong>Note:</strong> This is a demo. Please use test/sample data only.
      </p>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { getCustomerKycInfo } from "@/api/vivamo-api";

export default {
  name: "Verify",
  data() {
    return {
      loading: true,
      showWidget: false,
      terminalStatus: null,
      errorMessage: "",
      kycCompletedHandler: null,
    };
  },
  computed: {
    ...mapState(["vivamo", "kycStatus"]),
  },
  async mounted() {
    const userData = JSON.parse(localStorage.getItem("demoUserData") || "{}");
    const username = userData.username;

    if (!username) {
      this.$router.replace("/login");
      return;
    }

    try {
      await this.$store.dispatch("initializeVivamo");
    } catch (err) {
      console.error("Failed to initialize Vivamo SDK:", err);
      this.loading = false;
      this.terminalStatus = "failed";
      this.errorMessage = "Unable to load the verification widget.";
      return;
    }

    let kycInfo;
    try {
      kycInfo = await getCustomerKycInfo(username);
    } catch (err) {
      console.error("Failed to fetch KYC info:", err);
      this.loading = false;
      this.terminalStatus = "failed";
      this.errorMessage = "Unable to fetch verification details.";
      return;
    }

    const verification = kycInfo?.kycVerification;
    const status = verification?.status;
    const clientSecretKey = verification?.clientSecretKey;

    if (status === "verified" || status === "verified_manually") {
      this.loading = false;
      this.terminalStatus = "success";
      this.$store.commit("setKycStatus", status);
      setTimeout(() => this.$router.replace("/games"), 2000);
      return;
    }

    if (status === "under_review") {
      this.loading = false;
      this.terminalStatus = "review";
      this.$store.commit("setKycStatus", status);
      return;
    }

    if (status === "rejected" || status === "expired") {
      this.loading = false;
      this.terminalStatus = "failed";
      this.errorMessage =
        status === "expired"
          ? "Your verification link expired. Please contact support to retry."
          : "Your identity could not be verified.";
      return;
    }

    if (!clientSecretKey) {
      this.loading = false;
      this.terminalStatus = "failed";
      this.errorMessage =
        "No pending verification was found. Please contact support.";
      return;
    }

    this.kycCompletedHandler = ({ status: resultStatus } = {}) => {
      this.$store.commit("setKycStatus", resultStatus || null);
      this.showWidget = false;
      if (resultStatus === "verified" || resultStatus === "verified_manually") {
        this.terminalStatus = "success";
        setTimeout(() => this.$router.replace("/games"), 2000);
      } else if (resultStatus === "under_review") {
        this.terminalStatus = "review";
      } else {
        this.terminalStatus = "failed";
        this.errorMessage = "Your identity could not be verified.";
      }
    };
    this.vivamo.on("kyc-completed", this.kycCompletedHandler);

    this.loading = false;
    this.showWidget = true;
    this.$nextTick(() => {
      this.vivamo.createKycElements("#vv-kyc", clientSecretKey);
    });
  },
  beforeUnmount() {
    if (this.vivamo && this.kycCompletedHandler && this.vivamo.off) {
      this.vivamo.off("kyc-completed", this.kycCompletedHandler);
    }
  },
};
</script>

<style scoped>
.verify-container {
  max-width: 720px;
  margin: 0 auto;
  padding: 2rem 1rem;
  color: var(--text-light);
}

.verify-header {
  text-align: center;
  margin-bottom: 2rem;
}

.verify-header h1 {
  margin: 0 0 0.5rem;
}

.subtitle {
  color: var(--accent-color);
  font-size: 0.95rem;
}

.status-panel {
  background: var(--card-background);
  border: 1px solid var(--accent-color);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  margin-bottom: 1.5rem;
}

.status-panel.success {
  border-color: #22c55e;
}

.status-panel.review {
  border-color: #f59e0b;
}

.status-panel.error {
  border-color: #ef4444;
}

.spinner {
  width: 32px;
  height: 32px;
  margin: 0 auto 1rem;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: var(--accent-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.widget-wrap {
  background: var(--card-background);
  border: 1px solid var(--accent-color);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.return-button {
  display: inline-block;
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: var(--secondary-color);
  color: white;
  text-decoration: none;
  border-radius: 25px;
}

.help-section {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1.5rem;
}

.help-section h3 {
  margin-top: 0;
}

.help-section .note {
  margin-top: 1rem;
  font-size: 0.85rem;
  color: var(--accent-color);
}
</style>
